import type React from 'react';

export type PreviewActionTone = 'default' | 'danger';

export interface PreviewAction {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    onSelect: () => void;
    /** A divider separates adjacent actions whose groups differ. */
    group: string;
    tone?: PreviewActionTone;
    pending?: boolean;
    pendingLabel?: string;
    disabled?: boolean;
}

export type PreviewActionMenuEntry =
    { kind: 'item'; action: PreviewAction; index: number } | { kind: 'divider'; key: string };

export const layoutPreviewActionMenu = (
    actions: readonly PreviewAction[]
): PreviewActionMenuEntry[] =>
    actions.flatMap((action, index): PreviewActionMenuEntry[] => {
        const item: PreviewActionMenuEntry = { kind: 'item', action, index };
        const startsGroup = index > 0 && actions[index - 1].group !== action.group;
        return startsGroup ? [{ kind: 'divider', key: `divider-${action.id}` }, item] : [item];
    });

export const findPendingPreviewAction = (actions: readonly PreviewAction[]): PreviewAction | null =>
    actions.find((action) => action.pending) ?? null;

export type MenuNavigationKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

const MENU_NAVIGATION_KEYS: readonly string[] = ['ArrowDown', 'ArrowUp', 'Home', 'End'];

export const isMenuNavigationKey = (key: string): key is MenuNavigationKey =>
    MENU_NAVIGATION_KEYS.includes(key);

// Wraps at both ends; -1 (nothing focused yet) steps onto the first or last item.
export const nextMenuItemIndex = (
    current: number,
    count: number,
    key: MenuNavigationKey
): number => {
    if (count === 0) return -1;
    if (key === 'Home') return 0;
    if (key === 'End') return count - 1;
    if (current === -1) return key === 'ArrowDown' ? 0 : count - 1;
    const step = key === 'ArrowDown' ? 1 : -1;
    return (current + step + count) % count;
};
